import { type ReactNode } from 'react'
import * as TabsPrimitive from '@radix-ui/react-tabs'
import * as ToggleGroup from '@radix-ui/react-toggle-group'
import { cn } from './cn'

export interface TabItem<V extends string = string> { value: V; label: ReactNode; count?: number | null; icon?: ReactNode; disabled?: boolean }

export interface TabsProps<V extends string = string> {
  value: V
  onValueChange: (v: V) => void
  items: TabItem<V>[]
  /** Panels keyed by value. Omit to use Tabs purely as a switcher (e.g. saved views that change a query). */
  panels?: Partial<Record<V, ReactNode>>
  /** Keep inactive panels mounted (form state preserved). Default false. */
  keepMounted?: boolean
  /** Actions at the right end of the tab strip. */
  actions?: ReactNode
  className?: string
  listClassName?: string
  'aria-label'?: string
}

/**
 * Underlined tabs with optional counts. Keyboard: ←/→ move, Home/End.
 * `<Tabs value={tab} onValueChange={setTab} items={[{value:'all',label:'All',count:12}]} panels={{all:<…/>}} />`
 */
export function Tabs<V extends string = string>({ value, onValueChange, items, panels, keepMounted, actions, className, listClassName, ...aria }: TabsProps<V>) {
  return (
    <TabsPrimitive.Root value={value} onValueChange={v => onValueChange(v as V)} className={cn('flex min-w-0 flex-col', className)}>
      <div className={cn('flex min-w-0 items-end gap-2 border-b border-border', listClassName)}>
        <TabsPrimitive.List aria-label={aria['aria-label']} className="-mb-px flex min-w-0 flex-1 gap-4 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {items.map(t => (
            <TabsPrimitive.Trigger
              key={t.value}
              value={t.value}
              disabled={t.disabled}
              className={cn(
                'group relative inline-flex h-9 shrink-0 items-center gap-2 border-b-2 border-transparent px-1 text-base font-medium text-text-muted transition-colors duration-150',
                'hover:text-text data-[state=active]:border-text data-[state=active]:text-text disabled:opacity-50 [&_svg]:size-4',
                'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus rounded-t-[4px]',
              )}
            >
              {t.icon}
              {t.label}
              {t.count !== undefined && t.count !== null && (
                <span className="rounded-full bg-surface-active px-2 py-px text-xs font-medium text-text-muted tabular group-data-[state=active]:bg-text group-data-[state=active]:text-surface">
                  {t.count > 999 ? `${Math.floor(t.count / 1000)}k+` : t.count}
                </span>
              )}
            </TabsPrimitive.Trigger>
          ))}
        </TabsPrimitive.List>
        {actions && <div className="flex shrink-0 items-center gap-1 pb-1">{actions}</div>}
      </div>
      {panels && items.map(t => (panels[t.value] !== undefined ? (
        <TabsPrimitive.Content key={t.value} value={t.value} forceMount={keepMounted ? true : undefined}
          className="pt-4 outline-none data-[state=inactive]:hidden">
          {panels[t.value]}
        </TabsPrimitive.Content>
      ) : null))}
    </TabsPrimitive.Root>
  )
}

export interface SegmentedControlProps<V extends string = string> {
  value: V
  onValueChange: (v: V) => void
  options: Array<{ value: V; label: ReactNode; icon?: ReactNode; disabled?: boolean; 'aria-label'?: string }>
  size?: 'sm' | 'md'
  fullWidth?: boolean
  className?: string
  'aria-label'?: string
}

/**
 * Compact single-choice switcher (date grain, density, list/grid). Always has a value.
 * `<SegmentedControl value={grain} onValueChange={setGrain} options={[{value:'day',label:'Day'}]} />`
 */
export function SegmentedControl<V extends string = string>({ value, onValueChange, options, size = 'md', fullWidth, className, ...aria }: SegmentedControlProps<V>) {
  return (
    <ToggleGroup.Root
      type="single"
      value={value}
      onValueChange={v => { if (v) onValueChange(v as V) }}
      aria-label={aria['aria-label']}
      className={cn('inline-flex items-center gap-1 rounded-control bg-surface-active p-1', fullWidth && 'flex w-full', className)}
    >
      {options.map(o => (
        <ToggleGroup.Item
          key={o.value}
          value={o.value}
          disabled={o.disabled}
          aria-label={o['aria-label']}
          className={cn(
            'inline-flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-[4px] font-medium text-text-muted transition-[background-color,color,box-shadow] duration-150',
            'hover:text-text data-[state=on]:bg-surface data-[state=on]:text-text data-[state=on]:shadow-[0_1px_2px_rgb(0_0_0/0.08),0_0_0_1px_var(--border)]',
            'dark:data-[state=on]:bg-[#2a2a31] focus-visible:outline-2 focus-visible:outline-focus disabled:opacity-50 [&_svg]:size-3.5',
            size === 'sm' ? 'h-5 px-2 text-xs' : 'h-6 px-3 text-sm',
          )}
        >
          {o.icon}
          {o.label}
        </ToggleGroup.Item>
      ))}
    </ToggleGroup.Root>
  )
}
